import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import { useMockApi } from '@/lib/useMock'
import type { ResultsDestination } from '@/api/integrationsApi'
import {
  mockPdfDocument,
  mockReleaseAnswer,
  mockRetryScenario,
  type MockPreviewKind,
  type MockReleaseScenario,
} from '@/lib/lis/releaseMock'

/**
 * Outgoing LIS result mappings and the release of results to Epic
 * (z8nrz7ffzu / z8nrz7ffzv).
 *
 * Everything goes to OUR backend, never to the integration service: the
 * integration token stays server-side. The release calls honour
 * `VITE_USE_MOCK`, answering from `releaseMock.ts` until the backend route is
 * deployed; the mapping calls always hit the network.
 */

export type LisResultSourceType = 'form' | 'product'
export type LisResultValueType = 'NM' | 'ST' | 'TX'
export type LisFlagRule = 'none' | 'computed' | 'entered'
export type LisResultStatusCode = 'F' | 'P' | 'C'

/** ORC-12 / OBR-16. Epic matches it only by the NPI; names are optional. */
export type LisOrderingProvider = { npi: string; lastName?: string; firstName?: string; middleName?: string }

export type LisResultMappingRow = {
  id: string
  mappingId: string
  sortOrder: number
  sourceType: LisResultSourceType
  sourceField: string
  code: string
  text: string | null
  valueType: LisResultValueType
  units: string | null
  referenceRange: string | null
  flagRule: LisFlagRule
  /** A release with no value here is refused, naming it (z8nrz7fdc0). */
  required: boolean
}

export type LisResultMappingRowBody = Omit<LisResultMappingRow, 'id' | 'mappingId' | 'sortOrder'>

/**
 * Which form and product values this integration releases to Epic as result
 * lines (z8nrz7ffzv). Released UNSOLICITED (MedStar): nothing is ordered
 * first; the release is filed under the order code `testCode`.
 */
export type LisResultMapping = {
  id: string
  integrationId: string
  /** OBR-4.1, typed on the mapping, e.g. TBNK. */
  testCode: string
  testName: string | null
  formDefinitionId: string
  specimenSourceCode: string | null
  specimenSource: string | null
  /**
   * OBR-16 for this test's releases: they go unsolicited, so no order supplies
   * one. Null falls back to LIS Stages; with neither, a release is refused.
   */
  orderingProvider: LisOrderingProvider | null
  rows: LisResultMappingRow[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

export type LisResultMappingBody = {
  testCode?: string
  testName?: string | null
  formDefinitionId?: string
  specimenSourceCode?: string | null
  specimenSource?: string | null
  orderingProvider?: LisOrderingProvider | null
}

/**
 * Which already-generated PDF this integration sends to Epic (z8nrz7fgcp):
 * the file attached to `fieldId` on the completed form, sent UNSOLICITED (no
 * order is placed first) under the order code `testCode`, as one ED result
 * line `code^text^L`.
 */
export type LisPdfMapping = {
  id: string
  integrationId: string
  /** OBR-4.1, typed on the mapping, e.g. CTPROCRPT. */
  testCode: string
  testName: string | null
  sourceType: 'form_attachment'
  formDefinitionId: string
  fieldId: string
  code: string
  text: string | null
  /** OBR-16, as on a results mapping. */
  orderingProvider: LisOrderingProvider | null
  createdBy: string
  createdAt: string
  updatedAt: string
}

export type LisPdfMappingBody = Pick<
  LisPdfMapping,
  'testCode' | 'testName' | 'sourceType' | 'formDefinitionId' | 'fieldId' | 'code' | 'text' | 'orderingProvider'
>

export type LisMappableTest = { testCode: string; description: string | null }
export type LisProductResultField = { key: string; label: string }

/**
 * `not_set_up`: the integration service has no channel for the destination yet,
 * and nothing was sent. `undelivered`: no acknowledgement, may have reached Epic.
 */
export type LisReleaseStatus = 'pending' | 'accepted' | 'rejected' | 'undelivered' | 'not_set_up' | 'cancelled'

export type LisReleaseObservation = {
  code: string
  text?: string
  valueType: LisResultValueType
  value: string
  units?: string
  referenceRange?: string
  abnormalFlag?: string
}

export type LisResultRelease = {
  id: string
  orderId: string
  testCode: string
  destination: ResultsDestination
  resultStatus: LisResultStatusCode
  status: LisReleaseStatus
  ackCode: string | null
  controlId: string | null
  reason: string | null
  duplicate: boolean
  releasedBy: string
  releasedAt: string
  answeredAt: string | null
  /** What was sent. For a PDF release, which file: never its content. */
  payload?: { document?: { fileName: string; sizeBytes: number; sha256: string } | null }
}

/** The PDF a release would send, as the confirmation shows it. */
export type LisReleaseDocument = {
  fileName: string | null
  sizeBytes: number | null
  pageCount: number | null
  code: string
  text: string | null
  /** A short-lived link to open the file; null when there is nothing to open. */
  viewUrl: string | null
  /** Missing, not a PDF, too big… The release refuses with the same message. */
  problem: string | null
}

/** What Release to Epic would send for one product (no order needed). */
export type LisReleasePreview = {
  mappingId: string
  productId: string
  testCode: string
  testName: string | null
  destination: ResultsDestination
  integrationName: string
  resultStatus: LisResultStatusCode
  specimenSourceCode: string | null
  specimenSource: string | null
  /** What goes in OBR-16. */
  orderingProvider: LisOrderingProvider | null
  observations: LisReleaseObservation[]
  /** The result codes whose flag the tech chooses at release. */
  enteredFlagCodes: string[]
}

/** Results this product can release: a result mapping whose form is on its procedure. */
export type LisResultReport = {
  mappingId: string
  testCode: string
  testName: string | null
  formDefinitionId: string
  integrationName: string
  destination: ResultsDestination
  submission: { id: string; status: string }
  /** These results' releases for the product, newest first. */
  releases: LisResultRelease[]
}

/** A PDF this product can send: a PDF mapping whose form is on its procedure. */
export type LisPdfReport = {
  mappingId: string
  testCode: string
  testName: string | null
  code: string
  text: string | null
  formDefinitionId: string
  fieldId: string
  integrationName: string
  destination: ResultsDestination
  submission: { id: string; status: string }
  /** This PDF's sends for the product, newest first. */
  releases: LisResultRelease[]
}

export type LisPdfPreview = {
  mappingId: string
  productId: string
  testCode: string
  testName: string | null
  destination: ResultsDestination
  integrationName: string
  resultStatus: LisResultStatusCode
  /** What goes in OBR-16. */
  orderingProvider: LisOrderingProvider | null
  document: LisReleaseDocument
}

export type SendPdfBody = { mappingId: string; productId: string; resultStatus?: LisResultStatusCode }

export type ReleaseResultsBody = {
  mappingId: string
  productId: string
  resultStatus?: LisResultStatusCode
  /** The tech's flag for each `entered` row, by result code. */
  flags?: Record<string, string>
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders(init?.body ? { 'Content-Type': 'application/json' } : undefined),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
})

// ─── The in-app mock (VITE_USE_MOCK) ─────────────────────────────────────────

let mockScenario: MockReleaseScenario = 'accepted'
let mockPreviewKind: MockPreviewKind = 'results'
const mockReleases: LisResultRelease[] = []
const MOCK_DESTINATION: ResultsDestination = 'beaker'
const MOCK_PDF_MAPPING_ID = 'pdf-mock'
const MOCK_RESULTS_MAPPING_ID = 'results-mock'
const MOCK_RESULTS_ORDER_PREFIX = 'T21RES-'
const MOCK_PDF_ORDER_PREFIX = 'T21PDF-'

/** Dev only: which answer the mock gives next. */
export function setMockReleaseScenario(scenario: MockReleaseScenario) {
  mockScenario = scenario
}

export function getMockReleaseScenario(): MockReleaseScenario {
  return mockScenario
}

/** Dev only: whether the mock product has a PDF to send, and which file state it is in. */
export function setMockPreviewKind(kind: MockPreviewKind) {
  mockPreviewKind = kind
}

export function getMockPreviewKind(): MockPreviewKind {
  return mockPreviewKind
}

// Synthetic provider, never a real NPI.
const MOCK_PROVIDER: LisOrderingProvider = { npi: '1234567893', lastName: 'MOCKLAST', firstName: 'MOCKFIRST' }

const MOCK_OBSERVATIONS: LisReleaseObservation[] = [
  { code: 'CD3', text: 'CD3 %', valueType: 'NM', value: '72.4', units: '%' },
  { code: 'CD3AbsCt', text: 'CD3 Absolute', valueType: 'NM', value: '1450', units: 'cells/mcL' },
  { code: 'CD19', text: 'CD19 %', valueType: 'NM', value: '17.9', units: '%', referenceRange: '6-23', abnormalFlag: 'NORMAL' },
]

function mockControlId() {
  return `R01MOCK${Math.random().toString(36).slice(2, 10).toUpperCase()}`
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const lisResultsApi = {
  // Outgoing mappings, per LIS integration.
  listMappings: (integrationId: string) =>
    request<LisResultMapping[]>(`/integrations/lis/result-mappings?integrationId=${encodeURIComponent(integrationId)}`),

  /** Each LIS Stages test code once, with its description. */
  mappableTests: () => request<LisMappableTest[]>('/integrations/lis/result-mappings/tests'),

  productFields: () => request<LisProductResultField[]>('/integrations/lis/result-mappings/product-fields'),

  createMapping: (body: LisResultMappingBody & { integrationId: string; testCode: string; formDefinitionId: string }) =>
    request<LisResultMapping>('/integrations/lis/result-mappings', json('POST', body)),

  updateMapping: (id: string, body: LisResultMappingBody) =>
    request<LisResultMapping>(`/integrations/lis/result-mappings/${encodeURIComponent(id)}`, json('PATCH', body)),

  removeMapping: (id: string) =>
    request<{ removed: boolean }>(`/integrations/lis/result-mappings/${encodeURIComponent(id)}`, json('DELETE')),

  addRow: (mappingId: string, body: LisResultMappingRowBody) =>
    request<LisResultMapping>(`/integrations/lis/result-mappings/${encodeURIComponent(mappingId)}/rows`, json('POST', body)),

  updateRow: (rowId: string, body: Partial<LisResultMappingRowBody>) =>
    request<LisResultMapping>(`/integrations/lis/result-mappings/rows/${encodeURIComponent(rowId)}`, json('PATCH', body)),

  removeRow: (rowId: string) =>
    request<LisResultMapping>(`/integrations/lis/result-mappings/rows/${encodeURIComponent(rowId)}`, json('DELETE')),

  reorderRows: (mappingId: string, rowIds: string[]) =>
    request<LisResultMapping>(
      `/integrations/lis/result-mappings/${encodeURIComponent(mappingId)}/rows/reorder`,
      json('POST', { rowIds }),
    ),

  // PDF mappings (z8nrz7fgcp), per LIS integration.
  listPdfMappings: (integrationId: string) =>
    request<LisPdfMapping[]>(`/integrations/lis/result-mappings/pdf?integrationId=${encodeURIComponent(integrationId)}`),

  createPdfMapping: (body: LisPdfMappingBody & { integrationId: string }) =>
    request<LisPdfMapping>('/integrations/lis/result-mappings/pdf', json('POST', body)),

  updatePdfMapping: (id: string, body: Partial<LisPdfMappingBody>) =>
    request<LisPdfMapping>(`/integrations/lis/result-mappings/pdf/${encodeURIComponent(id)}`, json('PATCH', body)),

  removePdfMapping: (id: string) =>
    request<{ removed: boolean }>(`/integrations/lis/result-mappings/pdf/${encodeURIComponent(id)}`, json('DELETE')),

  // Results, unsolicited (MedStar): no order is placed first; the release mints one.

  /** The results a product can release: each result mapping whose form is on its procedure. */
  resultReports: (productId: string) => {
    if (useMockApi) {
      return Promise.resolve<LisResultReport[]>([
        {
          mappingId: MOCK_RESULTS_MAPPING_ID,
          testCode: 'TBNK',
          testName: 'TBNK PANEL',
          formDefinitionId: 'form-lab',
          integrationName: 'Beaker - LIS (mock)',
          destination: MOCK_DESTINATION,
          submission: { id: 'sub-lab-mock', status: 'completed' },
          releases: mockReleases.filter((r) => r.orderId.startsWith(`${MOCK_RESULTS_ORDER_PREFIX}${productId}`)).reverse(),
        },
      ])
    }
    return request<LisResultReport[]>(`/integrations/lis/result-reports?productId=${encodeURIComponent(productId)}`)
  },

  previewRelease: (body: ReleaseResultsBody) => {
    if (useMockApi) {
      return Promise.resolve<LisReleasePreview>({
        mappingId: body.mappingId,
        productId: body.productId,
        testCode: 'TBNK',
        testName: 'TBNK PANEL',
        destination: MOCK_DESTINATION,
        integrationName: 'Beaker - LIS (mock)',
        resultStatus: body.resultStatus ?? 'F',
        specimenSourceCode: 'HCT/P',
        specimenSource: 'HPC Apheresis',
        orderingProvider: MOCK_PROVIDER,
        observations: MOCK_OBSERVATIONS.map((o) =>
          o.code === 'CD3' && body.flags?.CD3 ? { ...o, abnormalFlag: body.flags.CD3 } : o,
        ),
        enteredFlagCodes: ['CD3'],
      })
    }
    return request<LisReleasePreview>('/integrations/lis/result-reports/preview', json('POST', body))
  },

  releaseResults: (body: ReleaseResultsBody) => {
    if (useMockApi) {
      const answer = mockReleaseAnswer(
        mockScenario,
        {
          id: crypto.randomUUID(),
          // Minted, as the backend does: no order was placed.
          orderId: `${MOCK_RESULTS_ORDER_PREFIX}${body.productId}-${mockReleases.length + 1}`,
          testCode: 'TBNK',
          resultStatus: body.resultStatus ?? 'F',
          destination: MOCK_DESTINATION,
          releasedAt: new Date().toISOString(),
          releasedBy: 'mock@example.test',
        },
        mockControlId(),
      )
      mockReleases.push(answer)
      return Promise.resolve(answer)
    }
    return request<LisResultRelease>('/integrations/lis/result-reports/send', json('POST', body))
  },

  // Unsolicited PDF (z8nrz7fgcp): no order is placed first; the send mints one.

  /** The PDFs a product can send: each PDF mapping whose form is on its procedure. */
  pdfReports: (productId: string) => {
    if (useMockApi) {
      const document = mockPdfDocument(mockPreviewKind)
      if (!document) return Promise.resolve<LisPdfReport[]>([])
      return Promise.resolve<LisPdfReport[]>([
        {
          mappingId: MOCK_PDF_MAPPING_ID,
          testCode: 'CTPROCRPT',
          testName: 'CELL THERAPY PROCESSING REPORT',
          code: document.code,
          text: document.text,
          formDefinitionId: 'form-report',
          fieldId: 'f-report',
          integrationName: 'Beaker - LIS (mock)',
          destination: MOCK_DESTINATION,
          submission: { id: 'sub-mock', status: mockPreviewKind === 'pdf_not_completed' ? 'active' : 'completed' },
          releases: mockReleases.filter((r) => r.orderId.startsWith(`${MOCK_PDF_ORDER_PREFIX}${productId}`)).reverse(),
        },
      ])
    }
    return request<LisPdfReport[]>(`/integrations/lis/pdf-reports?productId=${encodeURIComponent(productId)}`)
  },

  previewPdf: (body: SendPdfBody) => {
    if (useMockApi) {
      const document = mockPdfDocument(mockPreviewKind)
      if (!document) return Promise.reject(new Error('The mock is set to send test results, not a PDF.'))
      return Promise.resolve<LisPdfPreview>({
        mappingId: body.mappingId,
        productId: body.productId,
        testCode: 'CTPROCRPT',
        testName: 'CELL THERAPY PROCESSING REPORT',
        destination: MOCK_DESTINATION,
        integrationName: 'Beaker - LIS (mock)',
        resultStatus: body.resultStatus ?? 'F',
        orderingProvider: MOCK_PROVIDER,
        document,
      })
    }
    return request<LisPdfPreview>('/integrations/lis/pdf-reports/preview', json('POST', body))
  },

  sendPdf: (body: SendPdfBody) => {
    if (useMockApi) {
      const document = mockPdfDocument(mockPreviewKind)
      // The backend refuses a file it cannot send before calling anything.
      if (!document) return Promise.reject(new Error('The mock is set to send test results, not a PDF.'))
      if (document.problem) return Promise.reject(new Error(`${document.problem} Nothing was sent.`))
      const answer: LisResultRelease = {
        ...mockReleaseAnswer(
          mockScenario,
          {
            id: crypto.randomUUID(),
            // Minted, as the backend does: no order was placed.
            orderId: `${MOCK_PDF_ORDER_PREFIX}${body.productId}-${mockReleases.length + 1}`,
            testCode: 'CTPROCRPT',
            resultStatus: body.resultStatus ?? 'F',
            destination: MOCK_DESTINATION,
            releasedAt: new Date().toISOString(),
            releasedBy: 'mock@example.test',
          },
          mockControlId(),
        ),
        payload: { document: { fileName: document.fileName ?? '', sizeBytes: document.sizeBytes ?? 0, sha256: 'mock' } },
      }
      mockReleases.push(answer)
      return Promise.resolve(answer)
    }
    return request<LisResultRelease>('/integrations/lis/pdf-reports/send', json('POST', body))
  },

  /** Retry (undelivered) or Send again (not set up): same release, same destination. */
  retryRelease: (releaseId: string) => {
    if (useMockApi) {
      const i = mockReleases.findIndex((r) => r.id === releaseId)
      if (i < 0) return Promise.reject(new Error(`No result release '${releaseId}'`))
      const previous = mockReleases[i]
      const answer = {
        ...mockReleaseAnswer(mockRetryScenario(mockScenario, previous.status), previous, mockControlId()),
        payload: previous.payload,
      }
      mockReleases[i] = answer
      return Promise.resolve(answer)
    }
    return request<LisResultRelease>(
      `/integrations/lis/results/releases/${encodeURIComponent(releaseId)}/retry`,
      json('POST', {}),
    )
  },
}
